/**
 * OfflineContext.js
 * ===================
 * Provider global pour :
 *   - état online/offline (navigator.onLine + listeners)
 *   - compteur de drafts en attente
 *   - sync auto au retour réseau + visibilitychange + scheduler périodique
 *   - retry exponentiel des drafts en erreur (backoff via offlineStorage)
 *   - retry manuel par draft
 *   - bouton "Installer Homly" (prompt PWA natif)
 *
 * Hooks exposés :
 *   - useOffline() → { isOnline, pendingCount, errorCount, lastSyncAt, syncing,
 *                       syncNow(), retryDraft(uuid), refreshPending(),
 *                       installPWA, canInstall }
 */
import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { toast } from 'sonner';
import api from '../lib/api';
import {
    listDrafts,
    listPendingDrafts,
    listSyncableDrafts,
    markSyncing,
    markSynced,
    markSyncError,
    resetRetry,
    cleanupOldSyncedDrafts,
} from '../lib/offlineStorage';

const OfflineContext = createContext(null);

// Re-vérifie périodiquement s'il y a des drafts à syncer (backoff arrivé à échéance)
const RETRY_SCHEDULER_INTERVAL_MS = 30_000; // 30s

export function OfflineProvider({ children }) {
    const [isOnline, setIsOnline] = useState(typeof navigator !== 'undefined' ? navigator.onLine : true);
    const [pendingCount, setPendingCount] = useState(0);
    const [errorCount, setErrorCount] = useState(0);
    const [syncing, setSyncing] = useState(false);
    const [lastSyncAt, setLastSyncAt] = useState(null);
    const [deferredPrompt, setDeferredPrompt] = useState(null);
    const syncInProgress = useRef(false);

    // Refresh pending count from IndexedDB
    const refreshPending = useCallback(async () => {
        try {
            const all = await listDrafts();
            const pend = all.filter((d) => d.sync_status === 'pending' || d.sync_status === 'syncing');
            const err = all.filter((d) => d.sync_status === 'error');
            setPendingCount(pend.length + err.length);
            setErrorCount(err.length);
        } catch (e) {
            // IndexedDB unavailable (private mode), ignore
        }
    }, []);

    // Push UN draft → backend (POST ou PUT si déjà créé partiellement)
    const pushDraft = useCallback(async (d) => {
        await markSyncing(d.client_uuid);
        const body = { ...d.payload, client_uuid: d.client_uuid };
        let res;
        if (d.server_id) {
            res = await api.put(`/estimations/${d.server_id}`, body);
        } else {
            res = await api.post('/estimations/', body);
        }
        const serverId = res.data?.id || d.server_id;
        await markSynced(d.client_uuid, serverId);
    }, []);

    /**
     * Sync : push tous les drafts éligibles (pending + error dont backoff écoulé)
     * options:
     *   - silent: ne pas afficher les toasts d'info
     *   - toastOnSuccess: forcer un toast succès même en mode silent (utile pour
     *     les sync auto au retour réseau où l'utilisateur s'attend à un feedback)
     */
    const syncNow = useCallback(async ({ silent = false, toastOnSuccess = false } = {}) => {
        if (syncInProgress.current) return { synced: 0, errors: 0, skipped: 0 };
        if (!navigator.onLine) {
            if (!silent) toast.error('Vous êtes hors-ligne, sync impossible');
            return { synced: 0, errors: 0, skipped: 0 };
        }
        syncInProgress.current = true;
        setSyncing(true);

        let syncable;
        try {
            syncable = await listSyncableDrafts();
        } catch (e) {
            setSyncing(false);
            syncInProgress.current = false;
            return { synced: 0, errors: 0, skipped: 0 };
        }

        if (!syncable.length) {
            setSyncing(false);
            syncInProgress.current = false;
            setLastSyncAt(new Date().toISOString());
            return { synced: 0, errors: 0, skipped: 0 };
        }

        let synced = 0;
        let errors = 0;
        for (const d of syncable) {
            try {
                await pushDraft(d);
                synced += 1;
            } catch (e) {
                const msg = e?.response?.data?.detail || e?.message || 'Erreur inconnue';
                await markSyncError(d.client_uuid, msg);
                errors += 1;
            }
        }

        // Cleanup brouillons synced anciens
        try { await cleanupOldSyncedDrafts(); } catch (_) { /* ignore */ }

        setSyncing(false);
        syncInProgress.current = false;
        setLastSyncAt(new Date().toISOString());
        await refreshPending();

        // Toasts
        const showSuccess = !silent || toastOnSuccess;
        if (showSuccess && synced > 0) {
            toast.success(
                `${synced} découverte${synced > 1 ? 's' : ''} synchronisée${synced > 1 ? 's' : ''} ✓`
            );
        }
        if (!silent && errors > 0) {
            toast.error(`${errors} en erreur — nouvelle tentative automatique programmée`);
        }
        return { synced, errors, skipped: 0 };
    }, [pushDraft, refreshPending]);

    /**
     * Retry manuel d'un draft précis (utilisé depuis OfflineDraftsPanel).
     * Réinitialise le retry_count, force pending puis déclenche un sync.
     */
    const retryDraft = useCallback(async (uuid) => {
        try {
            await resetRetry(uuid);
            await refreshPending();
        } catch (_) { /* ignore */ }
        if (!navigator.onLine) {
            toast.error('Hors-ligne — la sync reprendra automatiquement au retour du réseau');
            return;
        }
        await syncNow();
    }, [refreshPending, syncNow]);

    // Boot-once flag pour la sync initiale (évite le warning set-state-in-effect)
    const bootedRef = useRef(false);

    // Listeners online/offline + visibility + scheduler retry
    useEffect(() => {
        const handleOnline = () => {
            setIsOnline(true);
            // Auto-sync au retour réseau, avec toast succès si quelque chose part
            syncNow({ silent: true, toastOnSuccess: true });
        };
        const handleOffline = () => {
            setIsOnline(false);
        };
        const handleVisibility = () => {
            // Quand l'utilisateur revient sur l'onglet/app (depuis arrière-plan)
            if (document.visibilityState === 'visible' && navigator.onLine) {
                syncNow({ silent: true, toastOnSuccess: true });
            }
        };
        // Boot once : refresh count + sync silencieuse si online. Encapsulé dans une
        // fonction async pour que la useEffect callback elle-même ne set-state pas
        // synchroniquement (compatible react-hooks/set-state-in-effect).
        const bootOnce = async () => {
            if (bootedRef.current) return;
            bootedRef.current = true;
            await refreshPending();
            if (navigator.onLine) {
                await new Promise((r) => setTimeout(r, 1500));
                await syncNow({ silent: true });
            }
        };

        window.addEventListener('online', handleOnline);
        window.addEventListener('offline', handleOffline);
        document.addEventListener('visibilitychange', handleVisibility);
        bootOnce();

        // Scheduler périodique : re-tente les drafts en error dont backoff arrive
        // à échéance + refresh du compteur
        const scheduler = setInterval(async () => {
            try {
                await refreshPending();
                if (!navigator.onLine || document.visibilityState !== 'visible') return;
                const syncable = await listSyncableDrafts();
                if (syncable.length > 0) {
                    await syncNow({ silent: true, toastOnSuccess: true });
                }
            } catch (_) { /* ignore */ }
        }, RETRY_SCHEDULER_INTERVAL_MS);

        return () => {
            window.removeEventListener('online', handleOnline);
            window.removeEventListener('offline', handleOffline);
            document.removeEventListener('visibilitychange', handleVisibility);
            clearInterval(scheduler);
        };
    }, [refreshPending, syncNow]);

    // Prompt PWA install
    useEffect(() => {
        const onBeforeInstall = (e) => {
            e.preventDefault();
            setDeferredPrompt(e);
        };
        window.addEventListener('beforeinstallprompt', onBeforeInstall);
        return () => window.removeEventListener('beforeinstallprompt', onBeforeInstall);
    }, []);

    const installPWA = useCallback(async () => {
        if (!deferredPrompt) {
            toast.info("L'installation n'est pas disponible sur ce navigateur. Sur iOS Safari : icône Partager → 'Sur l'écran d'accueil'.");
            return;
        }
        deferredPrompt.prompt();
        const choice = await deferredPrompt.userChoice;
        if (choice.outcome === 'accepted') {
            toast.success('Homly installé sur votre appareil 🎉');
        }
        setDeferredPrompt(null);
    }, [deferredPrompt]);

    const value = {
        isOnline,
        pendingCount,
        errorCount,
        syncing,
        lastSyncAt,
        syncNow,
        retryDraft,
        refreshPending,
        installPWA,
        canInstall: !!deferredPrompt,
    };

    return <OfflineContext.Provider value={value}>{children}</OfflineContext.Provider>;
}

export function useOffline() {
    const ctx = useContext(OfflineContext);
    if (!ctx) throw new Error('useOffline must be used within OfflineProvider');
    return ctx;
}

// Compatibility export (used by tests / panel)
export { listPendingDrafts };
